import { useRealtimeStore } from '@/config/stores/realtime-store';
import { useUIStore } from '@/config/stores/ui-store';
import { useAuthStore } from '@/features/auth/stores/auth.store';

import { getFriendsWithPresence } from '@/features/participant/services/contacts.service';
import { getDeviceId } from '@/shared/helpers';
import {
  HttpTransportType,
  type HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel
} from '@microsoft/signalr';
import { version } from '../../../package.json';
import { HUB_EVENTS, hubEmitter } from './hub-events';

// Constants
const KEEP_ALIVE_INTERVAL = 30000;
const SERVER_TIMEOUT = 60000;

/**
 * Hub Connection Manager
 * Manages SignalR connection lifecycle, retries, and token handling
 */
class HubConnectionManager {
  private connection: HubConnection | null = null;
  private isConnecting = false;
  private connectionPromise: Promise<void> | null = null;

  /**
   * Create new SignalR connection
   */
  private async createConnection(token: string): Promise<HubConnection> {
    if (this.connection?.state === HubConnectionState.Connected) {
      this.connection.stop();
    }

    if (!token) {
      throw new Error('No valid token available');
    }

    const baseUrl = `${import.meta.env.VITE_API_URL}${import.meta.env.VITE_WSS_PREFIX}/chatHub`;

    const connection = new HubConnectionBuilder()
      .withUrl(baseUrl, {
        transport: HttpTransportType.WebSockets,
        skipNegotiation: true,
        accessTokenFactory: () => localStorage.getItem('accessToken') || '',
        headers: {
          'x-device-platform': 'web',
          'x-device-id': getDeviceId(),
          'x-device-client-info': `v${version}`
        }
      })
      .withKeepAliveInterval(KEEP_ALIVE_INTERVAL)
      .withServerTimeout(SERVER_TIMEOUT)
      .withAutomaticReconnect() // Let SignalR handle basic reconnection
      .configureLogging(LogLevel.None)
      .build();

    this.setupEventHandlers(connection);
    return connection;
  }

  /**
   * Setup connection event handlers
   */
  private setupEventHandlers(connection: HubConnection): void {
    // Connection events
    connection.onreconnecting((error) => {
      console.warn('[HubConnection] Reconnecting:', error);
      hubEmitter.emit(HUB_EVENTS.CONNECTION.RECONNECTING, error);
    });

    connection.onreconnected(() => {
      console.info('[HubConnection] Reconnected');
      hubEmitter.emit(HUB_EVENTS.CONNECTION.RECONNECTED);
      this.resubscribeConversations();
      this.refreshFriendPresence();
    });

    connection.onclose((error) => {
      console.warn('[HubConnection] Disconnected:', error);
      hubEmitter.emit(HUB_EVENTS.CONNECTION.DISCONNECTED, error);
    });

    connection.on('ReceiveConnectionStatus', () => {
      const { activeBubbleConvs, activeConversation } = useUIStore.getState();

      const currentIds = new Set<string>();

      if (activeConversation?.conversationId) {
        currentIds.add(activeConversation.conversationId);
      }

      activeBubbleConvs.forEach((conv) => {
        if (conv.conversationId) {
          currentIds.add(conv.conversationId);
        }
      });

      // Subscribe to new conversations
      currentIds.forEach((id) => {
        this.subscribeToConversation(id);
      });
    });

    // Message events
    connection.on('ReceiveMessage', (data) => {
      hubEmitter.emit(HUB_EVENTS.MESSAGE.RECEIVED, data);
    });

    connection.on('ReceiveMessageRecalled', (data) => {
      hubEmitter.emit(HUB_EVENTS.MESSAGE.RECALLED, data);
    });

    connection.on('ReceiveMessageDeletedForUser', (data) => {
      hubEmitter.emit(HUB_EVENTS.MESSAGE.DELETED_FOR_USER, data);
    });

    connection.on('ReceiveMessagePinned', (data) => {
      hubEmitter.emit(HUB_EVENTS.MESSAGE.PINNED, data);
    });

    connection.on('ReceiveMessageUnpinned', (data) => {
      hubEmitter.emit(HUB_EVENTS.MESSAGE.UNPINNED, data);
    });

    connection.on('ReceiveReactionUpdated', (data) => {
      hubEmitter.emit(HUB_EVENTS.MESSAGE.REACTIONS_UPDATED, data);
    });

    connection.on('ReceiveConversationUpdated', (data) => {
      hubEmitter.emit(HUB_EVENTS.MESSAGE.CONVERSATION_UPDATED, data);
    });

    connection.on('ReceiveConversationRemoved', (data) => {
      hubEmitter.emit(HUB_EVENTS.MESSAGE.CONVERSATION_REMOVED, data);
    });

    connection.on('ReceiveConversationAdded', (data) => {
      hubEmitter.emit(HUB_EVENTS.MESSAGE.CONVERSATION_ADDED, data);
    });

    // Friend presence events
    connection.on('ReceiveFriendOnlineStatus', (data) => {
      useUIStore.getState().updateFriendPresence(data);
    });

    connection.on('ReceiveFriendOfflineStatus', (data) => {
      useUIStore.getState().updateFriendPresence(data);
    });
  }

  /**
   * Connect to SignalR hub
   */
  async connect(token: string): Promise<void> {
    // If already connected, do nothing
    if (this.connection?.state === HubConnectionState.Connected) {
      console.info('[HubConnection] Already connected, skipping...');
      return;
    }

    // If connection is in progress, wait for it
    if (this.isConnecting && this.connectionPromise) {
      console.info('[HubConnection] Connection in progress, waiting...');
      return this.connectionPromise;
    }

    // Set connecting flag and create promise
    this.isConnecting = true;
    this.connectionPromise = this.performConnect(token);

    try {
      await this.connectionPromise;
    } finally {
      this.isConnecting = false;
      this.connectionPromise = null;
    }
  }

  /**
   * Perform the actual connection
   */
  private async performConnect(token: string): Promise<void> {
    if (this.connection) {
      try {
        await this.forceDisconnect();
      } catch (error) {
        console.error('[HubConnection] Error disconnecting:', error);
      }
    }

    try {
      hubEmitter.emit(HUB_EVENTS.CONNECTION.CONNECTING);
      this.connection = await this.createConnection(token);
      await this.connection.start();
      console.info('[HubConnection] Connected successfully');
      hubEmitter.emit(HUB_EVENTS.CONNECTION.CONNECTED);

      // Store connection in realtime store
      useRealtimeStore.getState().setConnection(this.connection);
    } catch (error) {
      console.error('[HubConnection] Connection failed:', error);
      hubEmitter.emit(HUB_EVENTS.CONNECTION.CONNECTING);
      const authStore = useAuthStore.getState();

      const newToken = await authStore.refreshAccessToken();
      if (newToken) {
        setTimeout(() => {
          this.connect(newToken);
        }, 1000);
      }
    }
  }

  /**
   * Disconnect from SignalR hub (safe for HMR)
   */
  async disconnect(): Promise<void> {
    // Only disconnect if we have a connection and it's not already disconnected
    if (this.connection && this.connection.state !== HubConnectionState.Disconnected) {
      await this.forceDisconnect();
    }
  }

  /**
   * Force disconnect from SignalR hub (internal use)
   */
  private async forceDisconnect(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
        console.info('[HubConnection] Disconnected successfully');
      } catch (error) {
        console.error('[HubConnection] Disconnect error:', error);
      } finally {
        this.connection = null;
        useRealtimeStore.getState().disconnect();
        hubEmitter.emit(HUB_EVENTS.CONNECTION.DISCONNECTED);
      }
    }
  }

  /**
   * Check if token is expired and handle token refresh
   */
  async handleTokenExpiry(): Promise<void> {
    const authStore = useAuthStore.getState();

    console.info('[HubConnection] Token expired, refreshing...');

    // Disconnect current connection
    await this.disconnect();

    // Refresh token
    const newToken = await authStore.refreshAccessToken();
    if (!newToken) {
      throw new Error('Failed to refresh token');
    }

    hubEmitter.emit(HUB_EVENTS.TOKEN.REFRESHED);

    // Reconnect with new token
    await this.connect(newToken);
  }

  /**
   * Resubscribe to all conversations after reconnection
   */
  private async resubscribeConversations(): Promise<void> {
    const realtimeStore = useRealtimeStore.getState();
    const subscribedConversations = Array.from(realtimeStore.subscribedConversations);

    if (subscribedConversations.length === 0) {
      return;
    }

    console.info('[HubConnection] Resubscribing to conversations:', subscribedConversations);

    try {
      await Promise.allSettled(subscribedConversations.map((convId) => realtimeStore.subscribeToConversation(convId)));
      hubEmitter.emit(HUB_EVENTS.SUBSCRIPTION.RESUBSCRIBED, subscribedConversations);
    } catch (error) {
      console.error('[HubConnection] Failed to resubscribe conversations:', error);
    }
  }

  /**
   * Get current connection state
   */
  getConnectionState(): HubConnectionState | null {
    return this.connection?.state ?? null;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  /**
   * Subscribe to conversation
   */
  async subscribeToConversation(conversationId: string): Promise<void> {
    const realtimeStore = useRealtimeStore.getState();
    await realtimeStore.subscribeToConversation(conversationId);
    hubEmitter.emit(HUB_EVENTS.SUBSCRIPTION.SUBSCRIBED, conversationId);
  }

  /**
   * Unsubscribe from conversation
   */
  async unsubscribeFromConversation(conversationId: string): Promise<void> {
    const realtimeStore = useRealtimeStore.getState();
    await realtimeStore.unsubscribeFromConversation(conversationId);
    hubEmitter.emit(HUB_EVENTS.SUBSCRIPTION.UNSUBSCRIBED, conversationId);
  }

  /**
   * Subscribe to friend presence
   */
  async refreshFriendPresence(): Promise<void> {
    const friends = await getFriendsWithPresence();
    if (friends) {
      useUIStore.getState().setFriends(friends);
    }
  }
}

// Singleton instance
export const hubConnectionManager = new HubConnectionManager();
